import { groupBy } from "lodash-es";
import { ReactNode, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";

export type UsePlatformPoliciesFilterContextOutput = {
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function usePlatformPoliciesFilterContext(): UsePlatformPoliciesFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const searchFilterParam = searchParams.get("queryTerm");

  const searchFilter = searchFilterParam?.length
    ? searchFilterParam
    : undefined;

  const activeFilters = [
    searchFilter
      ? {
          label: "Search",
          value: searchFilter,
          onClick: () => removeFilter("queryTerm"),
        }
      : [],
  ].flatMap((f) => f);

  const addFilter = (param: string, value: string) => {
    const url = new URL(location.href);
    url.searchParams.set(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string) => {
    const url = new URL(location.href);
    url.searchParams.delete(param);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = new URL(location.href);
    url.searchParams.delete("queryTerm");
    navigate(url.href.replace(url.origin, ""));
  };

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = usePlatformPoliciesCategorySuggestions(searchInput);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter("queryTerm", filter);
    },
  });

  const activeFilterBar = renderActiveFilterBar(activeFilters, () =>
    resetFilters(),
  );

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar: null,
    activeFilterBar,
  });

  return {
    filterForm,
    filterBar,
    quickFilterBar: null,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return (
    <FilterBar
      {...props}
      sx={{ backgroundColor: "background.paper", borderRadius: 2 }}
    />
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function usePlatformPoliciesCategorySuggestions(searchInput: string) {
  const queryFilterGroup: FilterMenuOption = {
    field: "queryTerm",
    label: `Press enter to free text search "${searchInput}"`,
    value: "queryTerm",
    isDisabled: true,
  };

  const suggestions = [searchInput.length > 0 ? queryFilterGroup : undefined]
    .flatMap((g) => g ?? [])
    .filter((s) =>
      s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    );
  const loading = false;
  const error = false;

  return {
    suggestions,
    fetchSuggestions: (value?: string) => {},
    loading,
    error,
  };
}
